/**
 * PortraitEngine — shared Babylon.js engine for live 3D runner portraits.
 * All assets from R2/CDN. No fallbacks.
 *
 * Uses skin_config.json from CDN for:
 *  - Valid model whitelist (only models with skin config are rendered)
 *  - Selective skin recoloring (pickedColor + tolerance masking)
 *
 * Usage:
 *   const canvas = createPortraitCanvas(48);
 *   container.appendChild(canvas);
 *   renderPortraitToCanvas(canvas, 'cc_sk_chr_cyborg_female_01');
 */

import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader';
import { AssetContainer } from '@babylonjs/core/assetContainer';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';

import '@babylonjs/loaders/glTF';
import '@babylonjs/core/Animations/animatable';
import '@babylonjs/core/Materials/PBR';

import { resolveAsset } from '../data/AssetResolver';
import type { RunnerDTO, RecruitCandidate } from '../data/fixture';

const RENDER_SIZE = 256;
const MAX_DPR = 2;
const MAX_CONTAINERS = 20;

// ── Skin config from CDN ─────────────────────────────────────────────────────

interface SkinColorConfig {
  pickedColor: [number, number, number]; // RGB 0-255
  tolerance: number;
}

let skinConfig: Map<string, SkinColorConfig> | null = null;
const VALID_MODELS: Set<string> = new Set();
let skinConfigLoading: Promise<void> | null = null;

async function loadSkinConfig(): Promise<void> {
  if (skinConfig) return;
  if (skinConfigLoading) { await skinConfigLoading; return; }
  skinConfigLoading = (async () => {
    try {
      const resp = await fetch(resolveAsset('characters/skin_config'));
      const data = await resp.json() as Record<string, SkinColorConfig>;
      skinConfig = new Map();
      for (const [filename, config] of Object.entries(data)) {
        const name = filename.replace('.glb', '');
        skinConfig.set(name, config);
        VALID_MODELS.add(name);
      }
      console.debug('[PortraitEngine] skin_config loaded:', VALID_MODELS.size, 'models');
    } catch (err) {
      console.error('[PortraitEngine] Failed to load skin_config.json:', err);
    }
  })();
  await skinConfigLoading;
}

/**
 * Warm up the portrait engine: pre-loads skin_config.json so synchronous
 * resolveGlbName() calls have a populated VALID_MODELS set on first use.
 * Must be called AFTER initAssetResolver() (the skin config is now resolved
 * through the asset manifest), and AFTER the user is authenticated.
 *
 * Safe to call multiple times — loadSkinConfig() is idempotent.
 */
export async function warmupPortraitEngine(): Promise<void> {
  if (typeof document === 'undefined') return;
  try {
    const c = document.createElement('canvas');
    if (!c.getContext('webgl2') && !c.getContext('webgl')) return;
  } catch { return; }
  await loadSkinConfig();
}

// ── Skin tone palette (10 variants, indexed by PortraitSpec.skinIndex) ───────
// Target skin colors in RGB 0-255, used for texture-level recoloring.
const SKIN_TONES: [number, number, number][] = [
  [255, 227, 199],  // 0: light
  [245, 214, 184],  // 1: fair
  [224, 189, 153],  // 2: light tan
  [204, 166, 125],  // 3: tan
  [186, 145, 102],  // 4: medium
  [163, 122,  84],  // 5: olive
  [138, 102,  71],  // 6: brown
  [115,  82,  56],  // 7: dark brown
  [ 94,  64,  43],  // 8: deep brown
  [ 74,  48,  33],  // 9: very dark
];

// ── Head bone position from bone_hierarchy.json (cm, Z-up → Y-up at 0.01) ──
// Fallback if runtime bone lookup fails. Head bone at [0, 0.484, 156.911] cm.
// In Babylon Y-up with 0.01 root scale: Y = 156.911 * 0.01 = 1.569m
const HEAD_BONE_FALLBACK_Y = 1.569;

// ── Shared offscreen engine ──────────────────────────────────────────────────

let engine: Engine | null = null;
let scene: Scene | null = null;
let camera: ArcRotateCamera | null = null;
let offscreenCanvas: HTMLCanvasElement | null = null;
let initialized = false;

const containerCache = new Map<string, AssetContainer>();
/** Cache rendered portrait frames as data URLs to avoid re-rendering. */
const frameCache = new Map<string, string>();
const containerAccessOrder: string[] = [];

function init(): void {
  if (initialized) return;

  const dpr = Math.min(window.devicePixelRatio ?? 1, MAX_DPR);
  const pxSize = Math.round(RENDER_SIZE * dpr);

  offscreenCanvas = document.createElement('canvas');
  offscreenCanvas.width = pxSize;
  offscreenCanvas.height = pxSize;
  offscreenCanvas.style.display = 'none';

  engine = new Engine(offscreenCanvas, true, {
    preserveDrawingBuffer: true,
    stencil: false,
    antialias: true,
  });

  scene = new Scene(engine);
  scene.clearColor = new Color4(0.04, 0.04, 0.08, 1);
  scene.ambientColor = new Color3(0.15, 0.15, 0.2);

  const fill = new HemisphericLight('fill', new Vector3(0, 1, 0), scene);
  fill.intensity = 0.5;
  fill.groundColor = new Color3(0.12, 0.12, 0.16);

  const key = new DirectionalLight('key', new Vector3(-0.6, -0.3, -0.8), scene);
  key.intensity = 0.85;

  const fillDir = new DirectionalLight('fillDir', new Vector3(0.5, -0.1, -0.7), scene);
  fillDir.intensity = 0.3;

  const rim = new DirectionalLight('rim', new Vector3(0.8, -0.2, 0.6), scene);
  rim.intensity = 0.25;

  camera = new ArcRotateCamera('cam', -Math.PI / 2, Math.PI / 2.2, 3, Vector3.Zero(), scene);
  camera.minZ = 0.01;
  camera.maxZ = 50;

  initialized = true;
}

// ── Asset loading with LRU cache ─────────────────────────────────────────────

async function getContainer(url: string, cacheKey: string): Promise<AssetContainer> {
  const cached = containerCache.get(cacheKey);
  if (cached) {
    const idx = containerAccessOrder.indexOf(cacheKey);
    if (idx >= 0) containerAccessOrder.splice(idx, 1);
    containerAccessOrder.push(cacheKey);
    return cached;
  }

  if (!scene) throw new Error('[PortraitEngine] Not initialized');

  const container = await SceneLoader.LoadAssetContainerAsync('', url, scene);

  while (containerCache.size >= MAX_CONTAINERS && containerAccessOrder.length > 0) {
    const evict = containerAccessOrder.shift()!;
    containerCache.get(evict)?.dispose();
    containerCache.delete(evict);
  }

  containerCache.set(cacheKey, container);
  containerAccessOrder.push(cacheKey);
  return container;
}

// ── Scene management ─────────────────────────────────────────────────────────

function clearScene(): void {
  if (!scene) return;
  // Do NOT dispose materials globally — cached containers share them.
  // Mesh.dispose() already breaks the mesh→material reference.
  for (const m of scene.meshes.filter(m => m.name !== '__root__')) m.dispose();
  for (const n of scene.transformNodes.filter(n => n.name !== '__root__')) n.dispose();
  while (scene.skeletons.length > 0) scene.skeletons[0].dispose();
  while (scene.animationGroups.length > 0) scene.animationGroups[0].dispose();
}

function frameBust(meshes: AbstractMesh[]): void {
  if (!camera || meshes.length === 0) return;

  let minY = Infinity, maxY = -Infinity;
  let minX = Infinity, maxX = -Infinity;
  let minZ = Infinity, maxZ = -Infinity;

  for (const m of meshes) {
    m.computeWorldMatrix(true);
    const bi = m.getBoundingInfo();
    minY = Math.min(minY, bi.boundingBox.minimumWorld.y);
    maxY = Math.max(maxY, bi.boundingBox.maximumWorld.y);
    minX = Math.min(minX, bi.boundingBox.minimumWorld.x);
    maxX = Math.max(maxX, bi.boundingBox.maximumWorld.x);
    minZ = Math.min(minZ, bi.boundingBox.minimumWorld.z);
    maxZ = Math.max(maxZ, bi.boundingBox.maximumWorld.z);
  }

  const height = maxY - minY;
  if (height <= 0) return;

  const headTop = maxY;
  const bustBottom = minY + height * 0.78;
  const bustCenter = bustBottom + (headTop - bustBottom) * 0.45;

  camera.target = new Vector3((minX + maxX) / 2, bustCenter, (minZ + maxZ) / 2);
  camera.radius = height * 0.48;
  camera.alpha = Math.PI / 2;
  camera.beta = Math.PI / 2.05;
}

// ── Selective skin recoloring via texture pixel manipulation ──────────────────

function clamp255(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v)));
}

/**
 * Recolor skin-matching pixels in the albedo texture.
 * Uses the same approach as character-preview.ts (Settings screen):
 * - readPixels returns bottom-up data → flip Y before processing
 * - Weighted color distance for perceptual accuracy
 * - Luminance-preserving color replacement
 * - Flip Y back and update texture in-place via engine.updateTextureData
 */
async function recolorSkinTexture(
  pbr: PBRMaterial,
  pickedColor: [number, number, number],
  tolerance: number,
  targetColor: [number, number, number],
): Promise<void> {
  const tex = pbr.albedoTexture;
  if (!tex || !scene) return;

  const size = tex.getSize();
  const w = size.width, h = size.height;
  const readResult = tex.readPixels();
  if (!readResult) return;
  const gpuPixels = await readResult;
  if (!gpuPixels) return;

  const raw = new Uint8Array(gpuPixels instanceof ArrayBuffer ? gpuPixels : gpuPixels.buffer);

  // Flip Y: readPixels returns bottom-up, we need top-down for processing
  const rowBytes = w * 4;
  const pixels = new Uint8Array(raw.length);
  for (let y = 0; y < h; y++) {
    const srcOff = y * rowBytes;
    const dstOff = (h - 1 - y) * rowBytes;
    pixels.set(raw.subarray(srcOff, srcOff + rowBytes), dstOff);
  }

  const [sr, sg, sb] = pickedColor;
  const [tr, tg, tb] = targetColor;

  for (let i = 0; i < pixels.length; i += 4) {
    // Weighted color distance (perceptual, matches character-preview.ts)
    const dist = Math.sqrt(
      2 * (pixels[i] - sr) ** 2 +
      4 * (pixels[i + 1] - sg) ** 2 +
      3 * (pixels[i + 2] - sb) ** 2,
    );
    if (dist <= tolerance) {
      // Luminance-preserving replacement
      const origLum = (0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2]) / 255;
      const targetLum = (0.299 * tr + 0.587 * tg + 0.114 * tb) / 255;
      const lumRatio = targetLum > 0.01 ? origLum / targetLum : 1;
      const factor = 0.7 + 0.3 * Math.min(lumRatio, 1.5);
      pixels[i]     = clamp255(tr * factor);
      pixels[i + 1] = clamp255(tg * factor);
      pixels[i + 2] = clamp255(tb * factor);
    }
  }

  // Flip Y back to bottom-up for GPU upload
  const uploadData = new Uint8Array(pixels.length);
  for (let y = 0; y < h; y++) {
    uploadData.set(pixels.subarray(y * rowBytes, y * rowBytes + rowBytes), (h - 1 - y) * rowBytes);
  }

  // Update texture in-place (same as character-preview.ts)
  const eng = scene.getEngine();
  (eng as unknown as { updateTextureData(t: unknown, d: Uint8Array, x: number, y: number, w: number, h: number): void })
    .updateTextureData((tex as unknown as { _texture: unknown })._texture, uploadData, 0, 0, w, h);
}

// ── Render queue (serialize access to shared scene) ─────────────────────────

let renderQueue: Promise<void> = Promise.resolve();

/** Detect whether we're in a real browser with functional WebGL (not a stub like Happy-DOM). */
const hasWebGL = typeof document !== 'undefined' && (() => {
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2') || c.getContext('webgl');
    // Happy-DOM returns a stub context where core GL functions are missing
    return !!(gl && typeof (gl as WebGLRenderingContext).pixelStorei === 'function');
  } catch { return false; }
})();

function frameCacheKey(glbName: string, attachments?: string[], skinIndex?: number): string {
  let key = glbName;
  if (attachments?.length) key += `|${attachments.join(',')}`;
  if (skinIndex != null) key += `|s${skinIndex}`;
  return key;
}

function enqueueRender(target: HTMLCanvasElement, glbName: string, attachments?: string[], skinIndex?: number): Promise<void> {
  // Skip rendering entirely in environments without WebGL (test/SSR)
  if (!hasWebGL) return Promise.resolve();

  // Fast path: use cached frame if available
  const cacheKey = frameCacheKey(glbName, attachments, skinIndex);
  const cached = frameCache.get(cacheKey);
  if (cached) {
    const img = new Image();
    img.onload = () => {
      const ctx = target.getContext('2d');
      if (ctx) { ctx.clearRect(0, 0, target.width, target.height); ctx.drawImage(img, 0, 0, target.width, target.height); }
    };
    img.src = cached;
    return Promise.resolve();
  }

  const job = renderQueue.then(() => doRender(target, glbName, attachments, skinIndex)).catch((err) => {
    console.warn('[PortraitEngine] Render failed:', glbName, err);
  });
  renderQueue = job;
  return job;
}

// ── Public API ───────────────────────────────────────────────────────────────

/** Create a portrait canvas element at the given size. */
export function createPortraitCanvas(size: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  const dpr = Math.min(window.devicePixelRatio ?? 1, MAX_DPR);
  c.width = Math.round(size * dpr);
  c.height = Math.round(size * dpr);
  c.style.width = `${size}px`;
  c.style.height = `${size}px`;
  c.style.borderRadius = '50%';
  c.style.display = 'block';
  return c;
}

/**
 * Render a character portrait to a target canvas (queued — safe for concurrent calls).
 * glbName is the CDN filename without extension, e.g. "cc_sk_chr_cyborg_female_01"
 * attachments is an optional array of attachment GLB names (without extension).
 * skinIndex selects a skin tone variant (0–9).
 */
export function renderPortraitToCanvas(
  target: HTMLCanvasElement,
  glbName: string,
  attachments?: string[],
  skinIndex?: number,
): Promise<void> {
  return enqueueRender(target, glbName, attachments, skinIndex);
}

/** Internal render — must only run one at a time (enforced by queue). */
async function doRender(target: HTMLCanvasElement, glbName: string, attachments?: string[], skinIndex?: number): Promise<void> {
  init();
  if (!engine || !scene || !camera || !offscreenCanvas) {
    throw new Error('[PortraitEngine] WebGL not available');
  }

  // Ensure skin config is loaded (cached after first call)
  await loadSkinConfig();

  clearScene();

  // Validate model against skin_config whitelist
  let validGlb = glbName;
  if (VALID_MODELS.size > 0 && !VALID_MODELS.has(glbName)) {
    console.error(`[PortraitEngine] Model "${glbName}" NOT in skin_config.json! Valid: [${[...VALID_MODELS].join(', ')}]. Using fallback.`);
    validGlb = fallbackModel(glbName);
  }

  // Load base character
  const container = await getContainer(resolveAsset(`characters/${validGlb}`), `char:${validGlb}`);
  const entries = container.instantiateModelsToScene();
  const meshes = entries.rootNodes.flatMap(n =>
    n instanceof TransformNode ? (n as TransformNode).getChildMeshes() : [],
  );

  // Force opaque on base model
  for (const m of meshes) {
    if (m.material) {
      const pbr = m.material as PBRMaterial;
      pbr.transparencyMode = 0;
      pbr.alpha = 1;
    }
  }

  // Apply selective skin recoloring via texture manipulation
  const charConfig = skinConfig?.get(validGlb);
  if (skinIndex != null && charConfig) {
    const targetColor = SKIN_TONES[skinIndex % SKIN_TONES.length];
    for (const m of meshes) {
      if (m.material) {
        const pbr = m.material as PBRMaterial;
        if (pbr.albedoTexture) {
          try {
            await recolorSkinTexture(pbr, charConfig.pickedColor, charConfig.tolerance, targetColor);
          } catch (err) {
            console.warn('[PortraitEngine] Skin recolor failed:', validGlb, err);
          }
        }
      }
    }
  }

  // Find base mesh, skeleton and head bone for attachment parenting
  const baseMesh = meshes[0] ?? null;
  const baseSkeleton = baseMesh?.skeleton ?? null;
  const headBone = baseSkeleton?.bones.find(b => b.name === 'head') ?? null;

  // Attachment transform defaults — matches character-preview.ts (Settings screen).
  // Scale 100 compensates the base character's 0.01 root scale (FBX cm→m).
  // Rotation compensates FBX coordinate system difference.
  const ATT_SCALE = 100;
  const ATT_ROT_X = -90 * Math.PI / 180;
  const ATT_ROT_Y =  90 * Math.PI / 180;
  const ATT_ROT_Z = -180 * Math.PI / 180;

  // Load and attach accessories to head bone (same approach as Settings screen)
  if (attachments && attachments.length > 0) {
    const rootNode = entries.rootNodes[0] as TransformNode | undefined;
    const rootZ = rootNode?.scaling?.z ?? 1;
    const zFlip = rootZ < 0 ? -1 : 1;

    for (const attKey of attachments) {
      const attGlb = resolveAttachmentGlb(attKey);
      if (!attGlb) continue;
      try {
        const result = await SceneLoader.ImportMeshAsync('', resolveAsset(`attachments/${attGlb}`), '', scene);
        const attRoot = result.meshes[0];
        if (!attRoot) continue;

        if (headBone && baseMesh) {
          // attachToBone parents the mesh in bone-space — Babylon handles transforms
          attRoot.attachToBone(headBone, baseMesh);
          attRoot.scaling = new Vector3(ATT_SCALE, ATT_SCALE, zFlip * ATT_SCALE);
          attRoot.rotation = new Vector3(ATT_ROT_X, ATT_ROT_Y, ATT_ROT_Z);
          attRoot.position = Vector3.Zero();
        } else {
          // No skeleton — position at fallback head height
          attRoot.position = new Vector3(0, HEAD_BONE_FALLBACK_Y, 0);
        }

        for (const m of result.meshes) {
          meshes.push(m);
          m.isVisible = true;
          m.alwaysSelectAsActiveMesh = true;
          if (m.material) {
            (m.material as PBRMaterial).transparencyMode = 0;
            m.material.alpha = 1;
          }
        }
        console.debug('[PortraitEngine] Attachment:', attGlb,
          'meshes:', result.meshes.length,
          'bone:', headBone ? 'head' : 'fallback');
      } catch (err) {
        console.error('[PortraitEngine] Attachment load failed:', attGlb, err);
      }
    }
  }

  frameBust(meshes);
  await scene.whenReadyAsync();
  scene.render();

  // Copy to target canvas + cache the frame
  const ctx = target.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, target.width, target.height);
    ctx.drawImage(offscreenCanvas, 0, 0, target.width, target.height);
  }
  // Store rendered frame for instant reuse
  try {
    const dataUrl = offscreenCanvas.toDataURL('image/png');
    frameCache.set(frameCacheKey(glbName, attachments, skinIndex), dataUrl);
  } catch { /* canvas tainted or unavailable — skip caching */ }
}

// ── Model key → CDN GLB name resolution ─────────────────────────────────────

/**
 * Convert catalog model key to CDN GLB filename (without extension).
 * CyberCity: "Cyborg_Female" → "cc_sk_chr_cyborg_female_01"
 * Sci-Fi:    "Sci_Cop"       → "sf_sk_chr_cop_01"
 *
 * If the resolved name is not in the skin_config whitelist, falls back
 * to a deterministic valid model.
 */
export function resolveGlbName(modelKey: string): string {
  let name: string;
  if (modelKey.startsWith('Sci_')) {
    name = 'sf_sk_chr_' + modelKey.slice(4).toLowerCase() + '_01';
  } else {
    name = 'cc_sk_chr_' + modelKey.toLowerCase() + '_01';
  }
  // Validate against skin_config whitelist
  if (VALID_MODELS.size > 0 && !VALID_MODELS.has(name)) {
    return fallbackModel(modelKey);
  }
  return name;
}

/**
 * Deterministic fallback: pick a valid model based on hash of the original key.
 * Same input always produces the same output.
 */
function fallbackModel(key: string): string {
  const valid = [...VALID_MODELS];
  if (valid.length === 0) return 'cc_sk_chr_mercenary_male_01'; // absolute fallback
  const hash = key.split('').reduce((h, c) => (h * 31 + c.charCodeAt(0)) & 0x7fffffff, 0);
  return valid[hash % valid.length];
}

/**
 * Convert catalog attachment key to CDN GLB filename (without extension).
 * "SM_Chr_Attach_Hair_03" → "hair_03"
 * "SM_Chr_Attach_Cybernetics_Female_01" → "cybernetics_female_01"
 */
function resolveAttachmentGlb(attachKey: string): string {
  const prefix = 'SM_Chr_Attach_';
  if (!attachKey.startsWith(prefix)) return '';
  return attachKey.slice(prefix.length).toLowerCase();
}

// ── HTML helpers for game screens ───────────────────────────────────────────

/**
 * Return a portrait `<canvas>` placeholder as HTML string.
 * Call `hydratePortraits(container)` after inserting into the DOM.
 */
export function portraitHTML(runner: RunnerDTO, size: number): string {
  const model = runner.portrait?.model ?? '';
  const glb = model ? resolveGlbName(model) : '';
  const atts = (runner.portrait?.attachments ?? []).join(',');
  const skin = runner.portrait?.skinIndex ?? 0;
  const dpr = Math.min(window.devicePixelRatio ?? 1, MAX_DPR);
  const px = Math.round(size * dpr);
  return `<canvas class="portrait-canvas" data-glb="${glb}" data-attachments="${atts}" data-skin="${skin}" width="${px}" height="${px}" style="width:${size}px;height:${size}px;border-radius:50%;display:block;background:var(--bg-deep)"></canvas>`;
}

/**
 * Return a recruit portrait placeholder (no portrait spec → empty).
 */
export function recruitPortraitHTML(_candidate: RecruitCandidate, size: number): string {
  const dpr = Math.min(window.devicePixelRatio ?? 1, MAX_DPR);
  const px = Math.round(size * dpr);
  return `<canvas class="portrait-canvas" width="${px}" height="${px}" style="width:${size}px;height:${size}px;border-radius:50%;display:block;background:var(--bg-deep)"></canvas>`;
}

/**
 * Find all unrendered portrait canvases in `container` and kick off async rendering.
 */
export function hydratePortraits(container: HTMLElement): void {
  const canvases = container.querySelectorAll<HTMLCanvasElement>('canvas.portrait-canvas[data-glb]');
  for (const c of canvases) {
    const glb = c.dataset.glb;
    if (glb && !c.dataset.rendered) {
      c.dataset.rendered = '1';
      const atts = c.dataset.attachments ? c.dataset.attachments.split(',').filter(Boolean) : undefined;
      const skin = c.dataset.skin != null ? parseInt(c.dataset.skin, 10) : undefined;
      renderPortraitToCanvas(c, glb, atts, skin); // queued + error-logged internally
    }
  }
}

/** Dispose the shared engine and all cached assets. */
export function disposePortraitEngine(): void {
  for (const [, c] of containerCache) c.dispose();
  containerCache.clear();
  containerAccessOrder.length = 0;
  frameCache.clear();
  scene?.dispose();
  engine?.dispose();
  offscreenCanvas?.remove();
  engine = null;
  scene = null;
  camera = null;
  offscreenCanvas = null;
  initialized = false;
  renderQueue = Promise.resolve();
}
